// Filter controls shared by Audit › Changes and Audit › Logins.

import { useState } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { ExportResponse } from '@/api/types'
import { Button, DownloadIcon, FormField, InlineAlert, SelectInput, TextInput } from '@/components/ui'
import { useQuery } from '@/lib/query'

/**
 * "Export CSV": asks the server for a signed URL covering the WHOLE filtered
 * range (not the rows on screen) and sends the browser there to download.
 */
export function ExportCsvButton({ run }: { run: () => Promise<ExportResponse> }) {
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState<{ tone: 'warn' | 'bad'; text: string } | null>(null)
  const go = async () => {
    setPending(true)
    setMessage(null)
    try {
      const { url } = await run()
      window.location.assign(url)
    } catch (err) {
      setMessage({ tone: 'bad', text: `Couldn't export: ${errorMessage(err)}` })
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="grid gap-2">
      <Button onClick={go} loading={pending} icon={<DownloadIcon size={15} />}>
        Export CSV
      </Button>
      {message ? (
        <InlineAlert tone={message.tone} className="sm:max-w-xs">
          {message.text}
        </InlineAlert>
      ) : null}
    </div>
  )
}

/** Tenant picker (names, not free-text ids). */
export function TenantSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, status } = useQuery(['tenants', 'picker'], (signal) => api.tenants.list({ page_size: 100 }, signal))
  const options = [
    { value: '', label: status === 'loading' ? 'Loading tenants…' : 'All tenants' },
    ...(data?.leagues ?? []).map((t) => ({ value: String(t.id), label: t.name })),
  ]
  // Keep a URL value that isn't in the list (deleted tenant, >100 tenants) selectable.
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: `Tenant #${value}` })
  return (
    <FormField label="Tenant">
      {({ id }) => <SelectInput id={id} value={value} options={options} onChange={(e) => onChange(e.target.value)} />}
    </FormField>
  )
}

/** Inclusive date range. Both ends are whole days in the viewer's time zone. */
export function DateRangeFields({ from, to, onChange, error }: { from: string; to: string; onChange: (patch: { from?: string; to?: string }) => void; error?: string | null }) {
  return (
    <>
      <FormField label="From">
        {({ id }) => <TextInput id={id} type="date" value={from} max={to || undefined} onChange={(e) => onChange({ from: e.target.value })} />}
      </FormField>
      <FormField label="To (inclusive)" error={error}>
        {({ id, describedBy, invalid }) => (
          <TextInput id={id} type="date" aria-describedby={describedBy} invalid={invalid} value={to} min={from || undefined} onChange={(e) => onChange({ to: e.target.value })} />
        )}
      </FormField>
    </>
  )
}
